import { API_BASE_URL, ENVIRONMENT } from "../../env";
import type {
    ConversationResponse,
    CreateConversationPayload,
    CreateMessageMultipartArgs,
    CreateMessagePayload,
    CreateThemePayload,
    MessageResponse,
    MessagesAroundResponse,
    ThemeResponse,
    UUID,
} from "./chat.types";

export const WS_PATH = (import.meta.env.VITE_WS_PATH as string | undefined) ||
    "/ws/conversations";

const toWsBase = (httpBase: string) =>
    httpBase.replace(/^https:/i, "wss:").replace(/^http:/i, "ws:");

export const WS_BASE_URL = toWsBase(API_BASE_URL);

const isDev = ENVIRONMENT === "DEV";
const log = (...args: any[]) => {
    if (isDev) console.log("[api]", ...args);
};

export const getAccessToken = (): string | null =>
    localStorage.getItem("access_token");

export const getUserId = (): string | null => localStorage.getItem("user_id");

export const authHeaders = (): Record<string, string> => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const assertOk = async (res: Response) => {
    if (res.ok) return;
    const text = await res.text().catch(() => "");
    throw new Error(
        `[api] ${res.status} ${res.statusText}${text ? ` - ${text}` : ""}`,
    );
};

const apiUrl = (path: string) =>
    `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

const getJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const res = await fetch(apiUrl(path), {
        method: "GET",
        headers: { ...authHeaders(), ...(init?.headers || {}) },
        ...init,
    });
    await assertOk(res);
    return (await res.json()) as T;
};

const postJson = async <T>(
    path: string,
    body: unknown,
    init?: RequestInit,
): Promise<T> => {
    const res = await fetch(apiUrl(path), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
            ...(init?.headers || {}),
        },
        body: JSON.stringify(body),
        ...init,
    });
    await assertOk(res);
    return (await res.json()) as T;
};

const postForm = async <T>(
    path: string,
    form: FormData,
    init?: RequestInit,
): Promise<T> => {
    const res = await fetch(apiUrl(path), {
        method: "POST",
        headers: { ...authHeaders(), ...(init?.headers || {}) },
        body: form,
        ...init,
    });
    await assertOk(res);
    return (await res.json()) as T;
};

/**
 * XHR-based form upload with real upload progress tracking.
 * fetch() does NOT support upload progress — XMLHttpRequest.upload.onprogress is the only way.
 * Progress is capped at 99% until the server responds (matching onboarding behavior).
 */
const postFormWithProgress = <T>(
    path: string,
    form: FormData,
    onProgress?: (percent: number) => void,
    timeoutMs = 300_000, // 5 min, same as onboarding
    signal?: AbortSignal,
): Promise<T> =>
    new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
        }

        const xhr = new XMLHttpRequest();
        xhr.open("POST", apiUrl(path));
        xhr.timeout = timeoutMs;

        const hdrs = authHeaders();
        Object.entries(hdrs).forEach(([k, v]) =>
            xhr.setRequestHeader(k, v),
        );

        // Wire AbortSignal → xhr.abort()
        const onAbort = () => xhr.abort();
        signal?.addEventListener("abort", onAbort);
        const cleanup = () => signal?.removeEventListener("abort", onAbort);

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && e.total > 0) {
                    onProgress(
                        Math.min(Math.round((e.loaded * 100) / e.total), 99),
                    );
                }
            };
        }

        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText) as T);
                } catch {
                    reject(new Error("[api] Invalid JSON response"));
                }
            } else {
                reject(
                    new Error(
                        `[api] ${xhr.status} ${xhr.statusText}${xhr.responseText ? ` - ${xhr.responseText}` : ""}`,
                    ),
                );
            }
        };

        xhr.onerror = () => { cleanup(); reject(new Error("Network error")); };
        xhr.ontimeout = () => { cleanup(); reject(new Error("Request timed out")); };
        xhr.onabort = () => { cleanup(); reject(new DOMException("Aborted", "AbortError")); };
        xhr.send(form);
    });

export const buildConversationWsUrl = (conversationId: UUID): string => {
    const token = getAccessToken();
    if (!token) throw new Error("[api] Missing access token for websocket");

    const url = new URL(`${WS_BASE_URL}${WS_PATH}/${conversationId}`);
    url.searchParams.set("token", token);
    return url.toString();
};

export const listMyThemes = async (): Promise<ThemeResponse[]> =>
    getJson<ThemeResponse[]>("/themes");

export const createTheme = async (
    payload: CreateThemePayload,
): Promise<ThemeResponse> =>
    postJson<ThemeResponse>("/themes", {
        title: payload.title,
        is_onboarding: payload.is_onboarding ?? false,
        priority: payload.priority ?? null,
    });

export const getCanonicalConversation = async (): Promise<ConversationResponse> =>
    getJson<ConversationResponse>("/conversations/canonical");

export const listConversationsByTheme = async (
    themeId: UUID,
): Promise<ConversationResponse[]> => {
    const url = new URL(apiUrl("/conversations"));
    url.searchParams.set("theme_id", themeId);
    url.searchParams.set("limit", "50");
    url.searchParams.set("offset", "0");

    const res = await fetch(url.toString(), { headers: { ...authHeaders() } });
    await assertOk(res);
    return (await res.json()) as ConversationResponse[];
};

export const createConversation = async (
    payload: CreateConversationPayload,
): Promise<ConversationResponse> =>
    postJson<ConversationResponse>("/conversations", {
        theme_id: payload.theme_id,
        title: payload.title ?? null,
    });

export const listMessages = async (
    conversationId: UUID,
    limit: number = 10,
    before?: string,
    after?: string,
): Promise<MessageResponse[]> => {
    const url = new URL(apiUrl("/messages"));
    url.searchParams.set("conversation_id", conversationId);
    url.searchParams.set("limit", String(limit));
    if (before) url.searchParams.set("before", before);
    if (after) url.searchParams.set("after", after);

    const res = await fetch(url.toString(), { headers: { ...authHeaders() } });
    await assertOk(res);
    return (await res.json()) as MessageResponse[];
};

export const listMessagesAround = async (
    conversationId: UUID,
    messageId: UUID,
    beforeCount: number = 4,
    afterCount: number = 5,
): Promise<MessagesAroundResponse> => {
    const url = new URL(apiUrl("/messages/around"));
    url.searchParams.set("conversation_id", conversationId);
    url.searchParams.set("message_id", messageId);
    url.searchParams.set("before_count", String(beforeCount));
    url.searchParams.set("after_count", String(afterCount));

    const res = await fetch(url.toString(), { headers: { ...authHeaders() } });
    await assertOk(res);
    return (await res.json()) as MessagesAroundResponse;
};

export const createMessageJson = async (
    payload: CreateMessagePayload,
): Promise<MessageResponse> => postJson<MessageResponse>("/messages", payload);

export const createMessageMultipart = async (
    args: CreateMessageMultipartArgs,
): Promise<MessageResponse> => {
    const {
        conversation_id,
        content,
        files,
        audio,
        onUploadProgress,
        transcribe_pending,
        audio_duration,
        signal,
    } = args;

    const selected = files?.length ? files : [];
    if (selected.length === 0 && !audio) {
        return createMessageJson({
            conversation_id,
            content: (content || "").trim(),
        });
    }

    const fd = new FormData();
    fd.append("conversation_id", conversation_id);
    fd.append("content", content || "");
    for (const file of selected) {
        fd.append("files", file);
    }
    if (audio) fd.append("audio", audio);
    if (transcribe_pending) fd.append("transcribe_pending", "true");
    if (audio_duration != null) fd.append("audio_duration", String(audio_duration));

    // Use XHR for real upload progress when callback is provided
    if (onUploadProgress) {
        return postFormWithProgress<MessageResponse>(
            "/messages/multipart",
            fd,
            onUploadProgress,
            300_000,
            signal,
        );
    }
    return postForm<MessageResponse>("/messages/multipart", fd);
};

export const createMessageMultipartAll = async (
    args: CreateMessageMultipartArgs,
): Promise<MessageResponse[]> => {
    const { conversation_id, content, files, audio } = args;

    const selected = files?.length ? files : [];
    if (selected.length === 0 && !audio) {
        return [
            await createMessageJson({
                conversation_id,
                content: (content || "").trim(),
            }),
        ];
    }

    const fd = new FormData();
    fd.append("conversation_id", conversation_id);
    fd.append("content", content || "");
    for (const file of selected) {
        fd.append("files", file);
    }
    if (audio) fd.append("audio", audio);

    return [await postForm<MessageResponse>("/messages/multipart", fd)];
};

log("ENVIRONMENT =", ENVIRONMENT);
log("API_BASE_URL =", API_BASE_URL);
log("WS_BASE_URL =", WS_BASE_URL);
log("WS_PATH =", WS_PATH);
